import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import useMediaQuery from "@mui/material/useMediaQuery";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { useTheme } from "@mui/material/styles";
import { replaceAll, reportCalculater, toTitleCase } from "../utility/tools";
import { Box, Grid, Skeleton } from "@mui/material";
import List from "@mui/material/List";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import ListItem from "@mui/material/ListItem";
import Divider from "@mui/material/Divider";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";

export default function Report(props) {
  const { open, setOpen, transactions, userId } = props;
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [report, setReport] = React.useState({});
  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };
  React.useEffect(() => {
    if (open) getreport();
  }, [open]);

  const getreport = () => {
    const report = reportCalculater(transactions);
    if (report) setReport(report);
  };
  const nameIdExtract = (str) => {
    const arr = str.split("#");
    return {
      id: arr[0],
      name: arr[1],
    };
  };
  const relativePaymentKey = (str) => {
    const arr = str.split("-to-");
    return {
      from: nameIdExtract(arr[0]),
      to: nameIdExtract(arr[1]),
    };
  };

  // Filter pending payments to only show rows involving current user
  const filteredPendingPayments = React.useMemo(() => {
    if (!report?.pending_payments || !userId) return report?.pending_payments || {};
    const filtered = {};
    Object.entries(report.pending_payments).forEach(([key, val]) => {
      if (val === 0) return;
      const { from, to } = relativePaymentKey(key);
      if (String(from.id) === String(userId) || String(to.id) === String(userId)) {
        filtered[key] = val;
      }
    });
    return filtered;
  }, [report, userId]);

  // Calculate totals summary for current user
  const userSummary = React.useMemo(() => {
    if (!report?.pending_payments || !userId) return null;
    let totalToReceive = 0;
    let totalToPay = 0;
    const perPerson = {};

    Object.entries(report.pending_payments).forEach(([key, val]) => {
      if (val === 0) return;
      const { from, to } = relativePaymentKey(key);

      if (String(from.id) === String(userId)) {
        // I have to pay → from is me
        totalToPay += val;
        const personName = toTitleCase(replaceAll(to.name, "_", " "));
        if (!perPerson[personName]) perPerson[personName] = { toPay: 0, toReceive: 0 };
        perPerson[personName].toPay += val;
      } else if (String(to.id) === String(userId)) {
        // Someone pays me → to is me
        totalToReceive += val;
        const personName = toTitleCase(replaceAll(from.name, "_", " "));
        if (!perPerson[personName]) perPerson[personName] = { toPay: 0, toReceive: 0 };
        perPerson[personName].toReceive += val;
      }
    });

    // My contribution from created_payments
    let myTotalSpent = 0;
    let mySpentOnSelf = 0;
    if (report?.created_payments) {
      Object.entries(report.created_payments).forEach(([key, val]) => {
        const { id } = nameIdExtract(key);
        if (String(id) === String(userId)) {
          myTotalSpent = val.total_spent;
          mySpentOnSelf = val.spent_on_self;
        }
      });
    }

    return {
      totalToReceive,
      totalToPay,
      net: totalToReceive - totalToPay,
      myTotalSpent,
      mySpentOnSelf,
      perPerson,
      grandTotal: report.grand_total_expenses || 0,
    };
  }, [report, userId]);

  const headingStyle = {
    padding: "5px 10px",
    borderRadius: "5px",
    fontWeight: "600",
    color: "white",
    textAlign: "center",
  };
  const loader = (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Skeleton height={"400px"} width="100%" />
      </Grid>
      <Grid item xs={12} md={6}>
        <Skeleton height={"400px"} width="100%" />
      </Grid>
    </Grid>
  );
  return (
    <Dialog
      fullScreen={fullScreen}
      open={open}
      onClose={handleClose}
      aria-labelledby="responsive-dialog-title"
    >
      <DialogTitle
        id="responsive-dialog-title"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "15px",
          justifyContent: "center",
        }}
      >
        <ReceiptLongIcon />
        <span>Expense Report</span>
      </DialogTitle>
      <DialogContent>
        {Object.entries(report).length == 0 ? (
          loader
        ) : (
          <>
            {/* ===== EXPENSE SUMMARY (ON TOP) ===== */}
            {userSummary && (
              <Box sx={{ mb: 2.5 }}>
                <Typography
                  component="h4"
                  variant="body1"
                  color="text.primary"
                  sx={{ background: "#1565c0", ...headingStyle, mb: 1 }}
                >
                  📊 Expense Summary
                </Typography>
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    overflow: "hidden",
                    bgcolor: "background.paper",
                  }}
                >
                  {/* Grand total */}
                  <SummaryRow label="Grand Total (all expenses)" value={`₹ ${userSummary.grandTotal.toFixed(2)}`} />
                  <Divider />
                  <SummaryRow label="You spent (paid by you)" value={`₹ ${userSummary.myTotalSpent.toFixed(2)}`} color="primary.main" />
                  <Divider />
                  <SummaryRow label="Your share (spent on self)" value={`₹ ${userSummary.mySpentOnSelf.toFixed(2)}`} />
                  <Divider />
                  <SummaryRow label="You have to receive" value={`₹ ${userSummary.totalToReceive.toFixed(2)}`} color="success.main" />
                  <Divider />
                  <SummaryRow label="You have to pay" value={`₹ ${userSummary.totalToPay.toFixed(2)}`} color="error.main" />
                  <Divider />
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      bgcolor: userSummary.net >= 0
                        ? theme.palette.mode === "dark"
                          ? "rgba(34, 197, 94, 0.15)"
                          : "rgba(46, 125, 50, 0.1)"
                        : theme.palette.mode === "dark"
                        ? "rgba(239, 68, 68, 0.15)"
                        : "rgba(211, 47, 47, 0.1)",
                    }}
                  >
                    <Typography variant="body2" fontWeight={700}>
                      Net: {userSummary.net >= 0 ? "You get back" : "You owe"}{" "}
                      <span
                        style={{
                          color: userSummary.net >= 0
                            ? theme.palette.mode === "dark"
                              ? "#4ade80"
                              : "#2e7d32"
                            : theme.palette.mode === "dark"
                            ? "#f87171"
                            : "#d32f2f",
                          fontSize: "16px",
                          fontWeight: 800,
                        }}
                      >
                        ₹ {Math.abs(userSummary.net).toFixed(2)}
                      </span>
                    </Typography>
                  </Box>

                  {/* Per person breakdown */}
                  {Object.keys(userSummary.perPerson).length > 0 && (
                    <>
                      <Divider />
                      <Box sx={{ px: 2, py: 1, bgcolor: "action.hover" }}>
                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                          PER PERSON BREAKDOWN
                        </Typography>
                      </Box>
                      {Object.entries(userSummary.perPerson).map(([name, data]) => (
                        <React.Fragment key={name}>
                          <Divider />
                          <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography variant="body2">{name}</Typography>
                            <Box sx={{ textAlign: "right" }}>
                              {data.toReceive > 0 && (
                                <Typography variant="caption" sx={{ color: "success.main", display: "block", fontWeight: 600 }}>
                                  Receive: ₹{data.toReceive.toFixed(2)}
                                </Typography>
                              )}
                              {data.toPay > 0 && (
                                <Typography variant="caption" sx={{ color: "error.main", display: "block", fontWeight: 600 }}>
                                  Pay: ₹{data.toPay.toFixed(2)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </React.Fragment>
                      ))}
                    </>
                  )}
                </Box>
              </Box>
            )}

            {/* ===== CONTRIBUTORS & PENDING PAYMENTS GRID ===== */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography
                    component="h2"
                    variant="body1"
                    color="text.primary"
                    sx={{ background: "#01579b", ...headingStyle }}
                  >
                    Contributors
                  </Typography>
                  <Collabrators
                    created_payments={report?.created_payments}
                    nameIdExtract={nameIdExtract}
                    relativePaymentKey={relativePaymentKey}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ minWidth: "250px" }}>
                  <Typography
                    component="h4"
                    variant="body1"
                    color="text.primary"
                    sx={{ background: "#e65100", ...headingStyle }}
                  >
                    Pending Payments {userId ? "(Your Related)" : ""}
                  </Typography>
                  <PendingPayments
                    pending_payments={filteredPendingPayments}
                    nameIdExtract={nameIdExtract}
                    relativePaymentKey={relativePaymentKey}
                    userId={userId}
                  />
                </Box>
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" autoFocus onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SummaryRow({ label, value, color }) {
  return (
    <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={600} sx={{ color: color || "text.primary" }}>{value}</Typography>
    </Box>
  );
}

export function PendingPayments({
  pending_payments,
  nameIdExtract,
  relativePaymentKey,
  userId,
}) {
  const entries = Object.entries(pending_payments || {}).filter(([, val]) => val > 0);

  if (entries.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">No pending payments for you!</Typography>
      </Box>
    );
  }

  return (
    <List sx={{ width: "100%", bgcolor: "background.paper" }}>
      {entries.map(([key, val]) => {
        const { from, to } = relativePaymentKey(key);
        const isMe = userId && (String(from.id) === String(userId) || String(to.id) === String(userId));
        return (
          <>
            <ListItem key={from.id + to.id} alignItems="flex-start" sx={{ bgcolor: isMe ? "action.hover" : "transparent" }}>
              <ListItemText
                primary={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "nowrap",
                      justifyContent: "space-between",
                      gap: "8px",
                    }}
                  >
                    <div>
                      Id:{""}
                      {from.id}
                      <br></br>
                      <span >
                        {toTitleCase(replaceAll(from.name, "_", " "))}
                      </span>
                    </div>
                    <KeyboardDoubleArrowRightIcon sx={{ color: "#FF8C00" }} />
                    <div>
                      Id:{""}
                      {to.id}
                      <br></br>
                      <span >
                        {toTitleCase(replaceAll(to.name, "_", " "))}
                      </span>
                    </div>
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography
                      component="h6"
                      variant="body1"
                      color="text.primary"
                    >
                      <strong>Have to pay: </strong>
                      <strong
                        style={{
                          color: val > 0 ? "red" : "green",
                          letterSpacing: "1px",
                        }}
                      >
                        ₹ {val}
                      </strong>
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
            <Divider />
          </>
        );
      })}
    </List>
  );
}

export function Collabrators({
  created_payments,
  nameIdExtract,
  relativePaymentKey,
}) {
  return (
    <List sx={{ width: "100%", maxWidth: 360, bgcolor: "background.paper" }}>
      {Object.entries(created_payments).map(([key, val]) => {
        const { id, name } = nameIdExtract(key);
        return (
          <>
            <ListItem alignItems="flex-start" key={id + name}>
              <ListItemAvatar>
                <Avatar alt={name} />
              </ListItemAvatar>
              <ListItemText
                primary={
                  <strong>{toTitleCase(replaceAll(name, "_", " "))}</strong>
                }
                secondary={
                  <Box>
                    <Typography
                      component="h6"
                      variant="body1"
                      color="text.primary"
                    >
                      Total pay:{" "}
                      <strong
                        style={{
                          color: val.total_spent < 0 ? "red" : "green",
                          letterSpacing: "1px",
                        }}
                      >
                        ₹ {val.total_spent}
                      </strong>
                    </Typography>
                    <Typography
                      component="p"
                      variant="body1"
                      color="text.primary"
                    >
                      Spent on self:{" "}
                      <strong
                        style={{
                          color: (val.total_spent - val.spent_on_self) < 0 ? "red" : "green",
                          letterSpacing: "1px",
                        }}
                      >
                        ₹ {val.spent_on_self}{` ${val.total_spent - val.spent_on_self < 0 ? "-ve" : "+ve"}`}

                      </strong>
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
            <Divider />
          </>
        );
      })}
    </List>
  );
}
